/* ============================================================
   YUVENLIFE — Storefront API Client  (vanilla, zero-dep)
   ------------------------------------------------------------
   Low-level GraphQL-Client für die Shopify Storefront-API.
   Verantwortung NUR: Netzwerk + Resilienz + Normalisierung.
   Kein DOM, kein State. Exponiert window.YuvenStorefront.

   Resilienz lebt hier im request()-Kern:
   · AbortController-Timeout (8s)
   · Retry + exponentielles Backoff + Jitter (max 3) — nur bei
     Netzfehler / Timeout / HTTP 5xx / 429 / GraphQL THROTTLED.
   · NIE Retry bei userErrors (deterministische Business-Fehler).
   · Jede Operation gibt normalisiert zurück:
     { ok, cart|nodes, userErrors:[{code,field,message}], networkError|null }
     → der Aufrufer sieht NIE einen rohen throw.
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.YUVENLIFE_SHOP;
  if (!CFG) { console.error("[yuven-storefront] YUVENLIFE_SHOP config fehlt — Client inaktiv."); return; }

  var ENDPOINT  = "https://" + CFG.shopDomain + "/api/" + CFG.apiVersion + "/graphql.json";
  var TIMEOUT   = 8000;   // ms pro Versuch
  var MAX_TRIES = 3;
  var DBG       = !!(CFG.flags && CFG.flags.debug);

  function log() { if (DBG) console.log.apply(console, ["[yuven-sf]"].concat([].slice.call(arguments))); }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function backoff(tryNo) { return Math.pow(2, tryNo - 1) * 400 + Math.random() * 250; } // 400/800/1600ms + jitter
  function retryableHttp(s) { return s === 429 || (s >= 500 && s <= 599); }

  /* ── geteiltes Cart-Fragment: liefert checkoutUrl + Totals + Lines aus JEDER Mutation ── */
  var CART_FIELDS =
    "fragment CartFields on Cart {" +
    "  id checkoutUrl totalQuantity" +
    "  cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } totalTaxAmount { amount currencyCode } }" +
    "  discountCodes { code applicable }" +
    "  lines(first: 50) { nodes {" +
    "    ... on CartLine {" +
    "      id quantity" +
    "      sellingPlanAllocation { sellingPlan { id name } priceAdjustments { price { amount currencyCode } } }" +
    "      cost { totalAmount { amount currencyCode } }" +
    "      discountAllocations { discountedAmount { amount currencyCode } }" +
    "      merchandise { ... on ProductVariant {" +
    "        id title sku availableForSale price { amount currencyCode }" +
    "        product { id title vendor productType featuredImage { url altText } }" +
    "      } }" +
    "    }" +
    "    ... on ComponentizableCartLine {" +
    "      id quantity" +
    "      cost { totalAmount { amount currencyCode } }" +
    "      discountAllocations { discountedAmount { amount currencyCode } }" +
    "      merchandise { ... on ProductVariant {" +
    "        id title sku availableForSale price { amount currencyCode }" +
    "        product { id title vendor productType featuredImage { url altText } }" +
    "      } }" +
    "      lineComponents { id quantity }" +
    "    }" +
    "  } }" +
    "}";

  /* ── Kern-Request mit Timeout + Retry ──────────────────────────────── */
  function request(query, variables) {
    var tryNo = 0;
    function run() {
      tryNo++;
      var ctrl  = ("AbortController" in window) ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT) : null;
      return fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Shopify-Storefront-Access-Token": CFG.storefrontToken
        },
        body: JSON.stringify({ query: query, variables: variables || {} }),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res.ok) {
          if (retryableHttp(res.status) && tryNo < MAX_TRIES) {
            var ra = parseFloat(res.headers.get("Retry-After"));
            var wait = !isNaN(ra) ? ra * 1000 : backoff(tryNo);
            log("HTTP " + res.status + " → retry in " + Math.round(wait) + "ms");
            return sleep(wait).then(run);
          }
          return { ok: false, data: null, networkError: { kind: "http", status: res.status } };
        }
        return res.json().then(function (json) {
          if (json.errors && json.errors.length) {
            var throttled = json.errors.some(function (e) { return e.extensions && e.extensions.code === "THROTTLED"; });
            if (throttled && tryNo < MAX_TRIES) { log("THROTTLED → retry"); return sleep(backoff(tryNo)).then(run); }
            return { ok: false, data: json.data || null, networkError: { kind: "graphql", errors: json.errors } };
          }
          return { ok: true, data: json.data, networkError: null };
        });
      }).catch(function (err) {
        if (timer) clearTimeout(timer);
        var aborted = err && err.name === "AbortError";
        if (tryNo < MAX_TRIES) { log((aborted ? "timeout" : "network") + " → retry"); return sleep(backoff(tryNo)).then(run); }
        return { ok: false, data: null, networkError: { kind: aborted ? "timeout" : "network", message: err && err.message } };
      });
    }
    return run();
  }

  /* ── Mutation-Payload → normalisiertes Cart-Result ─────────────────── */
  function extractCart(res, key) {
    if (res.networkError) return { ok: false, cart: null, userErrors: [], networkError: res.networkError };
    var payload = res.data && res.data[key];
    if (!payload) return { ok: false, cart: null, userErrors: [], networkError: { kind: "shape" } };
    var ue = payload.userErrors || [];
    return { ok: ue.length === 0, cart: payload.cart || null, userErrors: ue, networkError: null };
  }

  /* ── Line-Input bauen (sellingPlanId nur wenn Abo aktiviert) ───────── */
  function buildLine(plan, qty) {
    var m = CFG.map[plan];
    if (!m) return null;
    var line = { merchandiseId: m.variantGid, quantity: qty || 1 };
    if (CFG.flags.subscriptionsEnabled && m.type === "subscription" && m.sellingPlanGid) {
      line.sellingPlanId = m.sellingPlanGid;
    }
    return line;
  }

  /* ── Operationen ───────────────────────────────────────────────────── */
  function cartCreate(lines) {
    var q = CART_FIELDS +
      "mutation CartCreate($lines:[CartLineInput!],$country:CountryCode)" +
      " @inContext(country:$country){" +
      "  cartCreate(input:{lines:$lines}){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { lines: lines, country: CFG.countryCode }).then(function (r) { return extractCart(r, "cartCreate"); });
  }

  function cartLinesAdd(cartId, lines) {
    var q = CART_FIELDS +
      "mutation CartLinesAdd($cartId:ID!,$lines:[CartLineInput!]!){" +
      "  cartLinesAdd(cartId:$cartId,lines:$lines){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, lines: lines }).then(function (r) { return extractCart(r, "cartLinesAdd"); });
  }

  function cartLinesUpdate(cartId, lines) {
    var q = CART_FIELDS +
      "mutation CartLinesUpdate($cartId:ID!,$lines:[CartLineUpdateInput!]!){" +
      "  cartLinesUpdate(cartId:$cartId,lines:$lines){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, lines: lines }).then(function (r) { return extractCart(r, "cartLinesUpdate"); });
  }

  function cartLinesRemove(cartId, lineIds) {
    var q = CART_FIELDS +
      "mutation CartLinesRemove($cartId:ID!,$lineIds:[ID!]!){" +
      "  cartLinesRemove(cartId:$cartId,lineIds:$lineIds){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, lineIds: lineIds }).then(function (r) { return extractCart(r, "cartLinesRemove"); });
  }

  function cartDiscountCodesUpdate(cartId, discountCodes) {
    var q = CART_FIELDS +
      "mutation CartDiscountCodesUpdate($cartId:ID!,$discountCodes:[String!]!){" +
      "  cartDiscountCodesUpdate(cartId:$cartId,discountCodes:$discountCodes){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, discountCodes: discountCodes }).then(function (r) { return extractCart(r, "cartDiscountCodesUpdate"); });
  }

  function getCart(cartId) {
    var q = CART_FIELDS + "query GetCart($cartId:ID!){ cart(id:$cartId){...CartFields} }";
    return request(q, { cartId: cartId }).then(function (r) {
      if (r.networkError) return { ok: false, cart: null, networkError: r.networkError };
      return { ok: true, cart: (r.data && r.data.cart) || null, networkError: null };
    });
  }

  function fetchPrices(variantGids) {
    var q = "query Prices($ids:[ID!]!) @inContext(country:" + CFG.countryCode + "){" +
            "  nodes(ids:$ids){ ... on ProductVariant { id availableForSale price{amount currencyCode} } } }";
    return request(q, { ids: variantGids }).then(function (r) {
      if (r.networkError) return { ok: false, nodes: [], networkError: r.networkError };
      return { ok: true, nodes: (r.data && r.data.nodes) || [], networkError: null };
    });
  }

  /* Produktdetails fürs Popover: Beschreibung + Metafelder (custom.details /
     custom.abo_details, Storefront-Access PUBLIC_READ) + Bild + Live-Preis.
     Wolfgang pflegt die Inhalte im Shopify-Produkt-Editor. */
  function fetchProductDetails(variantGids) {
    var q = "query Details($ids:[ID!]!) @inContext(country:" + CFG.countryCode + "){" +
            "  nodes(ids:$ids){ ... on ProductVariant {" +
            "    id availableForSale price{amount currencyCode}" +
            "    product { title descriptionHtml featuredImage{url altText}" +
            "      details: metafield(namespace:\"custom\", key:\"details\"){ value type }" +
            "      aboDetails: metafield(namespace:\"custom\", key:\"abo_details\"){ value type }" +
            "    } } } }";
    return request(q, { ids: variantGids }).then(function (r) {
      if (r.networkError) return { ok: false, nodes: [], networkError: r.networkError };
      return { ok: true, nodes: (r.data && r.data.nodes) || [], networkError: null };
    });
  }

  window.YuvenStorefront = {
    endpoint:       ENDPOINT,
    buildLine:      buildLine,
    cartCreate:     cartCreate,
    cartLinesAdd:   cartLinesAdd,
    cartLinesUpdate:cartLinesUpdate,
    cartLinesRemove:cartLinesRemove,
    cartDiscountCodesUpdate: cartDiscountCodesUpdate,
    getCart:        getCart,
    fetchPrices:    fetchPrices,
    fetchProductDetails: fetchProductDetails
  };
})();


/* Native Shopify cart: Shopify owns standard cart events and checkout. */
(function () {
  'use strict';
  var SF = window.YuvenStorefront;
  if (!SF) return;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var origin = window.location.origin;
  function numericId(gid) { return String(gid || '').split('/').pop(); }
  function money(cents, currency) { return {amount: (Number(cents || 0) / 100).toFixed(2), currencyCode: currency}; }
  function normalize(cart) {
    var currency = cart.currency || 'EUR';
    var applications = (cart.cart_level_discount_applications || []).slice();
    var nodes = (cart.items || []).map(function (item) {
      var allocation = item.selling_plan_allocation;
      var discounts = item.line_level_discount_allocations || [];
      discounts.forEach(function (discount) { if (discount.discount_application) applications.push(discount.discount_application); });
      var price = item.final_price == null ? item.price : item.final_price;
      return {
        id: item.key, quantity: item.quantity,
        sellingPlanAllocation: allocation ? {
          sellingPlan: {id: 'gid://shopify/SellingPlan/' + allocation.selling_plan.id, name: allocation.selling_plan.name},
          priceAdjustments: [{price: money(price, currency)}]
        } : null,
        cost: {totalAmount: money(item.final_line_price == null ? item.line_price : item.final_line_price, currency)},
        discountAllocations: discounts.map(function (d) { return {discountedAmount: money(d.amount, currency)}; }),
        merchandise: {
          id: 'gid://shopify/ProductVariant/' + item.variant_id, title: item.variant_title || 'Default Title',
          sku: item.sku, availableForSale: true, price: money(price, currency),
          product: {id: 'gid://shopify/Product/' + item.product_id, title: item.product_title, vendor: item.vendor,
            productType: item.product_type, featuredImage: item.featured_image ? {url: item.featured_image.url, altText: item.featured_image.alt} : null}
        }
      };
    });
    var codes = (cart.discount_codes || []).map(function (code) { return {code: code.code, applicable: code.applicable}; });
    applications.forEach(function (application) {
      if (application.type === 'discount_code' && !codes.some(function (code) { return code.code.toUpperCase() === application.title.toUpperCase(); })) {
        codes.push({code: application.title, applicable: true});
      }
    });
    return {native: true, id: cart.token, checkoutUrl: origin + root + 'checkout', totalQuantity: cart.item_count,
      cost: {subtotalAmount: money(cart.original_total_price, currency), totalAmount: money(cart.total_price, currency), totalTaxAmount: money(0, currency)},
      discountCodes: codes, lines: {nodes: nodes}};
  }
  async function request(path, body) {
    var response = await fetch(root + path, {method: body ? 'POST' : 'GET', credentials: 'same-origin',
      headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
      body: body ? JSON.stringify(body) : undefined});
    var data = await response.json();
    if (!response.ok) { var error = new Error(data.description || data.message || 'Warenkorb konnte nicht aktualisiert werden.'); error.status = response.status; throw error; }
    return data;
  }
  async function run(operation) {
    try { var cart = await operation(); return {ok: true, cart: normalize(cart), userErrors: [], networkError: null}; }
    catch (error) { return {ok: false, cart: null, userErrors: error.status ? [{code: 'INVALID', message: error.message}] : [], networkError: error.status ? null : error}; }
  }
  function read() { return request('cart.js'); }
  function add(lines) {
    return run(async function () {
      await request('cart/add.js', {items: lines.map(function (line) {
        var item = {id: numericId(line.merchandiseId), quantity: line.quantity};
        if (line.sellingPlanId) item.selling_plan = numericId(line.sellingPlanId);
        return item;
      })});
      return read();
    });
  }
  SF.nativeCart = true;
  SF.getCart = function () { return run(read); };
  SF.cartCreate = add;
  SF.cartLinesAdd = function (cartId, lines) { return add(lines); };
  SF.cartLinesUpdate = function (cartId, lines) {
    return run(async function () {
      var cart;
      for (var i = 0; i < lines.length; i++) {
        var body = {id: lines[i].id, quantity: lines[i].quantity};
        if (lines[i].sellingPlanId) body.selling_plan = numericId(lines[i].sellingPlanId);
        cart = await request('cart/change.js', body);
      }
      return cart || read();
    });
  };
  SF.cartLinesRemove = function (cartId, ids) {
    return run(async function () { var updates = {}; ids.forEach(function (id) { updates[id] = 0; }); return request('cart/update.js', {updates: updates}); });
  };
  SF.cartDiscountCodesUpdate = function (cartId, codes) {
    return run(function () { return request('cart/update.js', {discount: codes.join(',')}); });
  };
})();
